import type { FeatureResultState, ProjectedFeature } from './project-features.js';

/** Minimum overall score for the STRONG band. */
export const SCORE_BAND_STRONG_MIN = 80;

/** Minimum overall score for the NEEDS_REVIEW band. */
export const SCORE_BAND_NEEDS_REVIEW_MIN = 65;

export type PreflightScoreBandCode = 'STRONG' | 'NEEDS_REVIEW' | 'AT_RISK';

/**
 * Map a 0–100 score to a band. When `hasUnanalyzed` is true the result is capped
 * at NEEDS_REVIEW — an incomplete analysis can never read as STRONG.
 *
 * Shared by the web UI, the API aggregate, and the scoring worker so a creative's
 * band is identical wherever it is computed.
 */
export function scoreToBand(score: number, hasUnanalyzed = false): PreflightScoreBandCode {
    const band: PreflightScoreBandCode =
        score >= SCORE_BAND_STRONG_MIN
            ? 'STRONG'
            : score >= SCORE_BAND_NEEDS_REVIEW_MIN
              ? 'NEEDS_REVIEW'
              : 'AT_RISK';
    if (hasUnanalyzed && band === 'STRONG') return 'NEEDS_REVIEW';
    return band;
}

/**
 * Cross-creative synthesis only earns its space when there's more than one creative
 * to compare. Shared by the report-detail insight gate and the market aggregate so
 * the dashboard and a single report apply the same threshold.
 */
export const MIN_SCORED_CREATIVES_FOR_INSIGHTS = 2;

/** A PARTIAL verdict counts as half a miss when ranking blockers. */
export const BLOCKER_PARTIAL_WEIGHT = 0.5;

const DEFAULT_BLOCKER_LIMIT = 5;

/** A criterion dragging scores down across a set of scored creatives. */
export interface CriterionBlocker {
    code: string | null;
    name: string;
    /**
     * Catalogue definition for the criterion-name hover tooltip. Null when the
     * creatives disagree on the copy (image vs video branding definitions).
     */
    definition: string | null;
    /** Representative DS weight (0–1 fraction or percent, as the DS emits it). */
    weight: number;
    /** Scored creatives where this criterion returned FAIL. */
    failedCount: number;
    /** Scored creatives where this criterion returned PARTIAL. */
    partialCount: number;
    /** Scored creatives where this criterion contributed a verdict (the denominator). */
    scoredAssets: number;
    /** Ranking heuristic: weight × misses, partials counted at half. */
    impact: number;
}

/** Ranking heuristic shared by the report view and the market aggregate. */
export function blockerImpact(
    weight: number,
    failedCount: number,
    partialCount: number,
): number {
    return weight * (failedCount + partialCount * BLOCKER_PARTIAL_WEIGHT);
}

function contributesVerdict(state: FeatureResultState): boolean {
    return state === 'PASS' || state === 'FAIL' || state === 'PARTIAL';
}

export type BlockerFeatureInput = Pick<
    ProjectedFeature,
    'code' | 'name' | 'definition' | 'weight' | 'state'
>;

/**
 * Aggregate the criteria hurting scores most across any set of scored creatives.
 * Only PASS/FAIL/PARTIAL verdicts count toward a criterion's denominator, and a
 * criterion qualifies as a blocker only when it missed at least once. Ranked by
 * {@link blockerImpact} so a heavily weighted miss outranks a trivial one.
 */
type BlockerAccumulator = Omit<CriterionBlocker, 'definition' | 'impact'> & {
    /** Distinct catalogue definitions seen; mixed image/video copy yields more than one. */
    definitions: Set<string>;
};

/** Tooltips only when every scored creative agrees — mixed image/video copy shows none. */
function soleDefinition(definitions: Set<string>): string | null {
    const [first = null] = definitions;
    return definitions.size === 1 ? first : null;
}

export function aggregateBlockers(
    featuresPerAsset: readonly BlockerFeatureInput[][],
    limit = DEFAULT_BLOCKER_LIMIT,
): CriterionBlocker[] {
    const byCriterion = new Map<string, BlockerAccumulator>();

    for (const features of featuresPerAsset) {
        for (const feature of features) {
            if (!contributesVerdict(feature.state)) continue;

            const key = feature.code ?? feature.name;
            const entry =
                byCriterion.get(key) ??
                ({
                    code: feature.code,
                    name: feature.name,
                    definitions: new Set<string>(),
                    weight: feature.weight,
                    failedCount: 0,
                    partialCount: 0,
                    scoredAssets: 0,
                } satisfies BlockerAccumulator);

            if (feature.definition) entry.definitions.add(feature.definition);
            entry.scoredAssets += 1;
            entry.weight = Math.max(entry.weight, feature.weight);
            if (feature.state === 'FAIL') entry.failedCount += 1;
            else if (feature.state === 'PARTIAL') entry.partialCount += 1;
            byCriterion.set(key, entry);
        }
    }

    return [...byCriterion.values()]
        .filter((blocker) => blocker.failedCount > 0 || blocker.partialCount > 0)
        .map(({ definitions, ...blocker }) => ({
            ...blocker,
            definition: soleDefinition(definitions),
            impact: blockerImpact(blocker.weight, blocker.failedCount, blocker.partialCount),
        }))
        .sort((a, b) => b.impact - a.impact || b.failedCount - a.failedCount)
        .slice(0, limit);
}
